/**
 * Session: sign-in (Apple, Google, email code, dev login — or, on desktop, through the system browser), the 15-minute
 * access token (refreshed before it expires and once more when the server rejects it), the caller's workspaces, and
 * onboarding (create a workspace, accept an invite, join one that allows my email's domain, invite people). Persists the session, last workspace and
 * workspace list so the app opens straight into cached content — offline included; it never blocks on the network at
 * startup.
 */
import type { AuthMethodsResponse, CreateWorkspaceResponse, JoinableWorkspace, Workspace } from "@bundle/contracts";
import { LOCKOUT_MIN, normalizeCode, passwordProblem, passwordProblemFor, securityErrorFor, type PasswordProblem, type SignInStep } from "../logic/accountSecurity.ts";
import { accessTokenClaims, isCompleteEmailCode, isValidEmail, needsRefresh, normalizeEmailCode, refreshDelay, REFRESH_RETRY_MS } from "../logic/auth.ts";
import { onboardingScreen, type OnboardingScreen, type WorkspaceRef } from "../logic/onboarding.ts";
import {
  ApiFailure,
  errorKind,
  type ApiClient,
  type AuthClient,
  type BrowserSignInError,
  type BrowserSignInStatus,
  type Clock,
  type KeyValueStorage,
  type PasswordSignIn,
} from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type AuthMethods = AuthMethodsResponse;

export type Session = {
  /** 15-minute access token (JWT). */
  token: string;
  /** Access token expiry (epoch ms). */
  expiresAt: number;
  userId: string;
  email: string;
  /** How the access token is renewed: the Better Auth session ("session") or dev login ("dev", local only). */
  kind: "session" | "dev";
  /** Last opened workspace (absent until the user has one). */
  workspace?: WorkspaceRef;
};

export type SignInError =
  | "network"
  | "invalid"
  | "invalidEmail"
  | "invalidCode"
  | "rateLimited"
  | "unavailable"
  | "google"
  /** Apple sign-in didn't finish (the redirect came back with an error). */
  | "apple"
  /** Browser sign-in: cancelled on the browser page. */
  | "cancelled"
  /** Browser sign-in: the attempt expired or was replaced (start again). */
  | "signInExpired"
  /** Browser sign-in: the code expired or was already used. */
  | "codeRejected"
  /** Browser sign-in: the browser couldn't be opened. */
  | "browser"
  /** Password step: wrong password, or no password on this address (never says which). */
  | "wrongPassword"
  /** Too many wrong passwords (or two-step codes): locked for LOCKOUT_MIN minutes; email codes still work. */
  | "locked"
  /** Two-step: the authenticator code is wrong (or was already used). */
  | "twoStepCode"
  /** Two-step: the backup code is wrong or was already used. */
  | "backupCode"
  /** Reset: the new password is refused (the rule is in `passwordProblem`). */
  | "password";

/** "browser" = opening the system browser; "verifying" also covers a pasted browser sign-in code. */
export type SignInBusy = "checking" | "sendingCode" | "verifying" | "google" | "apple" | "dev" | "browser" | "password" | "passkey" | "resetting";

/**
 * The sign-in steps beyond the email code (design "Round 2: sign-in steps"; state machine in
 * docs/features/account-sign-in.md). Absent = the methods (or the email code step, `emailStep`).
 * - password: "Use a password instead" with the email as a chip; `locked` after too many wrong passwords (the field
 *   is off and "Email me a code instead" becomes the main action).
 * - twoFactor: after a password, an email code, a provider redirect (?twoFactor=1) or a reset; `backup` = a backup
 *   code instead of the authenticator; `trustDevice` ticked by default (30 days). `afterReset` ends on resetDone.
 * - resetCode → resetPassword → resetDone: "Forgot password?" (code to the email, new password, Done).
 * - passkey: waiting behind the browser/OS sheet, cancelled (an info notice, not an error) or notFound.
 */
export type SignInFlow =
  | { step: "password"; email: string; locked: boolean }
  | { step: "twoFactor"; backup: boolean; trustDevice: boolean; afterReset?: { email: string; signedOutOthers: boolean } }
  | { step: "resetCode"; email: string }
  | { step: "resetPassword"; email: string; code: string; signOutOthers: boolean; problem?: PasswordProblem }
  | { step: "resetDone"; email: string; signedOutOthers: boolean }
  | { step: "passkey"; status: "waiting" | "cancelled" | "notFound" };

/**
 * Create account (/signup; design "6 · Create account"). Absent = A, the name and email (with Apple and Google).
 * - exists: the email already has an account ("Sign in instead", no code sent).
 * - code: B, the email code (emailStep "code" + codeEmail hold the address, as in sign-in); `name` goes with the code.
 * - secure: C, signed in to the new account: add a passkey or set a password (optional), then the workspace flow.
 */
export type SignupStep = { step: "exists"; email: string } | { step: "code"; name: string } | { step: "secure" };

export type SessionState = {
  /** "signingIn" = establishing a session (startup resume, after a code or a redirect). */
  status: "signedOut" | "signingIn" | "signedIn";
  session?: Session;
  error?: SignInError;
  /** Sign-in methods this deployment offers (undefined until loaded). */
  methods?: AuthMethods;
  /**
   * Present when this platform signs in through the system browser (desktop); the methods above are then unused.
   * `waiting`: the browser was opened and the app is waiting for it to hand back the sign-in.
   */
  browser?: { waiting: boolean };
  /** Loading the methods failed (offline / server down). */
  methodsFailed: boolean;
  /** Email-code flow: entering the address, or the code sent to `codeEmail`. */
  emailStep: "email" | "code";
  codeEmail?: string;
  /** A sign-in step beyond the email code (password, two-step, reset, passkey); see SignInFlow. */
  flow?: SignInFlow;
  /** Create account's step (see SignupStep); absent outside /signup. */
  signup?: SignupStep;
  /** The address sign-in opens with ("Sign in instead" on Create account). */
  signInEmail?: string;
  /** Minutes left of a lockout ("locked" error). */
  lockedMinutes?: number;
  busy?: SignInBusy;
  /** The caller's workspaces (undefined = never loaded on this device). */
  workspaces?: WorkspaceRef[];
  workspacesLoading: boolean;
  /** Invite being accepted (/invite/<token>), kept through sign-in and the Google redirect. */
  inviteToken?: string;
  /**
   * Workspaces I can join without an invite (they allow my verified email's domain), offered during onboarding and
   * in the switcher ("Join Acme"). Undefined until loaded; online only, never cached.
   */
  joinable?: JoinableWorkspace[];
  /** Id of the workspace being joined. */
  joining?: string;
  /** The user chose "Create workspace". */
  creatingWorkspace: boolean;
  /** A workspace was just created: open its #general and offer "Invite people" (once). */
  welcome?: { workspaceId: string; generalChannelId: string };
};

export type SessionDeps = {
  api: ApiClient;
  auth: AuthClient;
  storage: KeyValueStorage;
  clock: Clock;
  /**
   * Called right before the session switches to another workspace (switcher, create, accept invite). Web puts
   * /{slug} in the address bar so the URL (which names the workspace) and the session agree; native ignores it.
   */
  onWorkspaceOpened?: (ws: WorkspaceRef) => void;
  /** Passwords, the second step, password reset and passkeys (absent: email codes and providers only). */
  password?: PasswordSignIn;
};

export const SESSION_KEY = "bundle.session.v1";
export const WORKSPACES_KEY = "bundle.workspaces.v1";
export const INVITE_KEY = "bundle.invite.v1";
/** A rejection right after a successful refresh means the server won't take fresh tokens either: sign out. */
const MIN_REJECT_INTERVAL_MS = 10_000;

function withoutProblem<T extends { problem?: PasswordProblem }>(flow: T): T {
  const next = { ...flow };
  delete next.problem;
  return next;
}

function parseRef(v: unknown): WorkspaceRef | undefined {
  if (typeof v !== "object" || v === null) return undefined;
  const w = v as Partial<WorkspaceRef>;
  if (typeof w.id !== "string" || typeof w.slug !== "string") return undefined;
  return { id: w.id, slug: w.slug, name: typeof w.name === "string" ? w.name : w.slug };
}

function parseSession(raw: string | undefined): Session | undefined {
  if (raw === undefined) return undefined;
  try {
    const s = JSON.parse(raw) as Partial<Session> & { workspace?: unknown };
    if (typeof s.token !== "string" || typeof s.userId !== "string") return undefined;
    const session: Session = {
      token: s.token,
      userId: s.userId,
      email: typeof s.email === "string" ? s.email : "",
      expiresAt: typeof s.expiresAt === "number" ? s.expiresAt : 0,
      // Sessions saved before production sign-in were dev logins.
      kind: s.kind === "session" ? "session" : "dev",
    };
    const ws = parseRef(s.workspace);
    if (ws !== undefined) session.workspace = ws;
    return session;
  } catch {
    return undefined;
  }
}

function parseWorkspaces(raw: string | undefined, userId: string): WorkspaceRef[] | undefined {
  if (raw === undefined) return undefined;
  try {
    const v = JSON.parse(raw) as { userId?: unknown; workspaces?: unknown };
    if (v.userId !== userId || !Array.isArray(v.workspaces)) return undefined;
    const out: WorkspaceRef[] = [];
    for (const w of v.workspaces) {
      const ref = parseRef(w);
      if (ref !== undefined) out.push(ref);
    }
    return out;
  } catch {
    return undefined;
  }
}

const toRef = (w: Workspace): WorkspaceRef => ({ id: w.id, slug: w.slug, name: w.name });
const byName = (a: WorkspaceRef, b: WorkspaceRef) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

function signInError(e: unknown): SignInError {
  const kind = errorKind(e);
  if (kind === "network") return "network";
  if (kind === "rateLimited") return "rateLimited";
  return "invalid";
}

const BROWSER_SIGN_IN_ERRORS: readonly string[] = ["cancelled", "expired", "codeRejected", "invalidCode", "network", "browser", "failed"];

function fromBrowserError(error: BrowserSignInError): SignInError {
  switch (error) {
    case "cancelled":
      return "cancelled";
    case "expired":
      return "signInExpired";
    case "codeRejected":
      return "codeRejected";
    case "invalidCode":
      return "invalidCode";
    case "network":
      return "network";
    case "browser":
      return "browser";
    case "failed":
      return "invalid";
  }
}

/** A failed browser sign-in call: its BrowserSignInError (ApiFailure.code), else by error kind. */
function browserSignInError(e: unknown): SignInError {
  if (e instanceof ApiFailure && BROWSER_SIGN_IN_ERRORS.includes(e.code)) return fromBrowserError(e.code as BrowserSignInError);
  return signInError(e);
}

export class SessionViewModel extends ViewModel<SessionState> {
  private readonly deps: SessionDeps;
  private cancelRefresh: (() => void) | undefined;
  private refreshing: Promise<boolean> | undefined;
  private lastRefreshAt = 0;
  /** A Create account code is being verified: the new session opens on "Secure your account". */
  private signingUp = false;

  constructor(deps: SessionDeps) {
    const session = parseSession(deps.storage.get(SESSION_KEY));
    const invite = deps.storage.get(INVITE_KEY);
    const initial: SessionState = {
      status: session === undefined ? "signedOut" : "signedIn",
      methodsFailed: false,
      emailStep: "email",
      workspacesLoading: false,
      creatingWorkspace: false,
    };
    if (session !== undefined) {
      initial.session = session;
      const list = parseWorkspaces(deps.storage.get(WORKSPACES_KEY), session.userId);
      if (list !== undefined) initial.workspaces = list;
    }
    if (invite !== undefined && invite !== "") initial.inviteToken = invite;
    const browser = deps.auth.browser;
    if (browser !== undefined) initial.browser = { waiting: false };
    super(initial);
    this.deps = deps;
    // A session restored without its access token (browser sign-in never stores one) refreshes right away.
    deps.api.setAuthToken(session === undefined || session.token === "" ? undefined : session.token);
    if (session !== undefined) this.scheduleRefresh(session.expiresAt);
    this.onDispose(() => this.cancelRefresh?.());
    if (browser !== undefined) this.onDispose(browser.subscribe((s) => this.browserChanged(s)));
  }

  /** The top-level screen for the current state (`urlSlug`: workspace named by the URL, if any). */
  screen(urlSlug: string | undefined): OnboardingScreen {
    const s = this.state;
    const input: Parameters<typeof onboardingScreen>[0] = { signedIn: s.status === "signedIn", creating: s.creatingWorkspace };
    if (s.inviteToken !== undefined) input.inviteToken = s.inviteToken;
    if (s.workspaces !== undefined) input.workspaces = s.workspaces;
    if (s.session?.workspace !== undefined) input.lastWorkspace = s.session.workspace;
    if (urlSlug !== undefined) input.urlSlug = urlSlug;
    return onboardingScreen(input);
  }

  // ---------- Sign-in ----------

  /**
   * Loads which sign-in methods to offer (once; again after a failure). Browser sign-in has none to load: it picks up
   * an attempt already in progress instead (a reloaded window), or a session another window just created.
   */
  async loadMethods(): Promise<void> {
    const browser = this.deps.auth.browser;
    if (browser !== undefined) {
      let s: BrowserSignInStatus;
      try {
        s = await browser.status();
      } catch {
        return; // "Sign in with your browser" still works
      }
      if (this.state.status !== "signedOut") return;
      if (s.signedIn) await this.establishFromSession();
      else if (this.state.browser?.waiting !== s.signingIn) this.update({ browser: { waiting: s.signingIn } });
      return;
    }
    if (this.state.methods !== undefined) return;
    try {
      const methods = await this.deps.api.authMethods();
      this.update({ methods, methodsFailed: false });
    } catch {
      this.update({ methodsFailed: true });
    }
  }

  /** Step 1 of the email flow: send a code to `email`. */
  async sendCode(email: string): Promise<void> {
    const address = email.trim().toLowerCase();
    if (this.state.busy !== undefined) return;
    if (!isValidEmail(address)) {
      this.update({ error: "invalidEmail" });
      return;
    }
    this.patchSignIn({ busy: "sendingCode" });
    try {
      await this.deps.auth.sendEmailCode(address);
      this.patchSignIn({ emailStep: "code", codeEmail: address });
    } catch (e) {
      this.patchSignIn({ error: errorKind(e) === "server" ? "unavailable" : signInError(e) });
    }
  }

  /** Sends a new code to the same address. */
  resendCode(): Promise<void> {
    const email = this.state.codeEmail;
    if (email === undefined) return Promise.resolve();
    return this.sendCode(email);
  }

  /** Back from the code step to change the address. */
  editEmail(): void {
    this.patchSignIn({ emailStep: "email" });
  }

  /** Step 2: verify the 6-digit code and sign in. */
  async verifyCode(code: string): Promise<void> {
    const email = this.state.codeEmail;
    const digits = normalizeEmailCode(code);
    if (email === undefined || this.state.busy !== undefined) return;
    if (!isCompleteEmailCode(digits)) {
      this.update({ error: "invalidCode" });
      return;
    }
    this.patchSignIn({ busy: "verifying" });
    const signup = this.state.signup;
    const name = signup?.step === "code" && signup.name !== "" ? signup.name : undefined;
    let step: void | SignInStep;
    try {
      step = name === undefined ? await this.deps.auth.verifyEmailCode(email, digits) : await this.deps.auth.verifyEmailCode(email, digits, name);
    } catch (e) {
      const kind = errorKind(e);
      this.patchSignIn({ error: kind === "network" ? "network" : kind === "rateLimited" ? "rateLimited" : "invalidCode" });
      return;
    }
    this.signingUp = signup?.step === "code";
    try {
      await this.afterFirstStep(step ?? { step: "done" });
    } finally {
      this.signingUp = false;
    }
  }

  // ---------- Create account ----------

  /**
   * Create account, step A → Continue: asks whether the email already has an account (POST /v1/auth/signup/check).
   * Taken: "You already have an account" and no code is sent. Otherwise the sign-in code goes out (it creates the
   * account) and the typed name rides along with it.
   */
  async startSignup(name: string, email: string): Promise<void> {
    const address = email.trim().toLowerCase();
    if (this.state.busy !== undefined) return;
    if (!isValidEmail(address)) {
      this.update({ error: "invalidEmail" });
      return;
    }
    this.patchSignIn({ busy: "checking" });
    let exists: boolean;
    try {
      exists = (await this.deps.api.signupCheck(address)).exists;
    } catch (e) {
      this.patchSignIn({ error: errorKind(e) === "server" ? "unavailable" : signInError(e) });
      return;
    }
    if (exists) {
      this.patchSignIn({ signup: { step: "exists", email: address } });
      return;
    }
    this.patchSignIn({ busy: "sendingCode" });
    try {
      await this.deps.auth.sendEmailCode(address);
      this.patchSignIn({ emailStep: "code", codeEmail: address, signup: { step: "code", name: name.trim() } });
    } catch (e) {
      this.patchSignIn({ error: errorKind(e) === "server" ? "unavailable" : signInError(e) });
    }
  }

  /** Create account: "Use a different email" (from the taken notice or the code step), back to step A. */
  signupDifferentEmail(): void {
    const next: SessionState = { ...this.state, emailStep: "email" };
    delete next.signup;
    delete next.codeEmail;
    delete next.error;
    this.setState(next);
  }

  /** Create account → sign-in: "Sign in instead" (the taken address fills the email field) or "Sign in". */
  leaveSignup(): void {
    const signup = this.state.signup;
    if (signup?.step === "secure") return;
    const next: SessionState = { ...this.state, emailStep: "email" };
    if (signup?.step === "exists") next.signInEmail = signup.email;
    delete next.signup;
    delete next.codeEmail;
    delete next.error;
    delete next.flow;
    this.setState(next);
  }

  /** Sign-in → Create account ("New to Bundle? Create an account"): starts at step A. */
  openSignup(): void {
    const next: SessionState = { ...this.state, emailStep: "email" };
    delete next.signup;
    delete next.codeEmail;
    delete next.error;
    delete next.flow;
    delete next.signInEmail;
    this.setState(next);
  }

  /** "Secure your account": done (a passkey or password was added) or "Skip for now" → the workspace flow. */
  finishSignup(): void {
    if (this.state.signup === undefined) return;
    const next: SessionState = { ...this.state };
    delete next.signup;
    this.setState(next);
  }

  // ---------- Password, two-step, reset, passkeys ----------

  /** This platform offers passwords, two-step and reset (PasswordSignIn). */
  get canUsePassword(): boolean {
    return this.deps.password !== undefined;
  }

  /** This platform can sign in with a passkey (hidden where WebAuthn / Credential Manager isn't there). */
  get canUsePasskey(): boolean {
    const p = this.deps.password;
    return p !== undefined && (p.passkeysAvailable?.() ?? true);
  }

  /** "Use a password instead": the password step for the typed address (shown to everyone; no lookup). */
  passwordInstead(email: string): void {
    const address = email.trim().toLowerCase();
    if (this.deps.password === undefined || this.state.busy !== undefined) return;
    if (!isValidEmail(address)) {
      this.update({ error: "invalidEmail" });
      return;
    }
    this.patchSignIn({ flow: { step: "password", email: address, locked: false } });
  }

  /** Back to the methods ("Use a different email", "Back to sign in", Cancel on the passkey prompt). */
  backToStart(): void {
    if (this.state.flow?.step === "passkey" && this.state.flow.status === "waiting") this.deps.password?.cancelPasskey?.();
    const next: SessionState = { ...this.state, emailStep: "email" };
    delete next.flow;
    delete next.error;
    delete next.busy;
    delete next.lockedMinutes;
    this.setState(next);
  }

  /** The password step: sign in (generic error on a wrong password; locked after too many). */
  async signInWithPassword(password: string): Promise<void> {
    const p = this.deps.password;
    const flow = this.state.flow;
    if (p === undefined || flow?.step !== "password" || flow.locked || this.state.busy !== undefined) return;
    if (password === "") {
      this.update({ error: "wrongPassword" });
      return;
    }
    this.patchSignIn({ busy: "password" });
    let step: SignInStep;
    try {
      step = await p.signInWithPassword(flow.email, password);
    } catch (e) {
      const error = this.flowError(e, "wrongPassword");
      if (error === "locked") this.patchSignIn({ error, lockedMinutes: LOCKOUT_MIN, flow: { ...flow, locked: true } });
      else this.patchSignIn({ error });
      return;
    }
    this.codeEmailFor(flow.email);
    await this.afterFirstStep(step);
  }

  /** The password step's "Email me a code instead" (the main action while locked). */
  emailCodeInstead(): Promise<void> {
    const flow = this.state.flow;
    if (flow?.step !== "password") return Promise.resolve();
    const next: SessionState = { ...this.state };
    delete next.flow;
    delete next.lockedMinutes;
    this.setState(next);
    return this.sendCode(flow.email);
  }

  /** "Forgot password?": sends the reset code to the address at once (always succeeds: no enumeration). */
  async forgotPassword(): Promise<void> {
    const p = this.deps.password;
    const flow = this.state.flow;
    const email = flow?.step === "password" || flow?.step === "resetCode" ? flow.email : undefined;
    if (p === undefined || email === undefined || this.state.busy !== undefined) return;
    this.patchSignIn({ busy: "sendingCode" });
    try {
      await p.requestPasswordReset(email);
    } catch (e) {
      this.patchSignIn({ error: this.flowError(e, "invalid") });
      return;
    }
    this.patchSignIn({ flow: { step: "resetCode", email } });
  }

  /** Reset, step 1: the 6-digit code (checked by the server together with the new password). */
  submitResetCode(code: string): void {
    const flow = this.state.flow;
    const digits = normalizeEmailCode(code);
    if (flow?.step !== "resetCode") return;
    if (!isCompleteEmailCode(digits)) {
      this.update({ error: "invalidCode" });
      return;
    }
    this.patchSignIn({ flow: { step: "resetPassword", email: flow.email, code: digits, signOutOthers: true } });
  }

  /** Reset, step 2: "Sign out of other devices" (ticked by default). */
  setSignOutOthers(on: boolean): void {
    const flow = this.state.flow;
    if (flow?.step === "resetPassword") this.update({ flow: { ...flow, signOutOthers: on } });
  }

  /**
   * Reset, step 2: the new password. Then signs in with it, which asks for the second step when two-step is on
   * (a reset never skips it), and ends on Done. A wrong or expired code goes back to the code step.
   */
  async resetPassword(newPassword: string): Promise<void> {
    const p = this.deps.password;
    const flow = this.state.flow;
    if (p === undefined || flow?.step !== "resetPassword" || this.state.busy !== undefined) return;
    const local = passwordProblem(newPassword, flow.email);
    if (local !== undefined) {
      this.patchSignIn({ error: "password", flow: { ...flow, problem: local } });
      return;
    }
    this.patchSignIn({ busy: "resetting", flow: withoutProblem(flow) });
    let step: SignInStep;
    try {
      step = await p.resetPassword(flow.email, flow.code, newPassword, flow.signOutOthers);
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : undefined;
      const problem = passwordProblemFor(code);
      if (problem !== undefined) {
        this.patchSignIn({ error: "password", flow: { ...flow, problem } });
        return;
      }
      if (securityErrorFor(code) === "invalidCode") {
        this.patchSignIn({ error: "invalidCode", flow: { step: "resetCode", email: flow.email } });
        return;
      }
      this.patchSignIn({ error: this.flowError(e, "invalid") });
      return;
    }
    this.codeEmailFor(flow.email);
    const afterReset = { email: flow.email, signedOutOthers: flow.signOutOthers };
    if (step.step === "twoFactor") this.patchSignIn({ flow: { step: "twoFactor", backup: false, trustDevice: true, afterReset } });
    else this.patchSignIn({ flow: { step: "resetDone", ...afterReset } });
  }

  /** Done after a reset: "Continue to Bundle" (the session already exists). */
  finishReset(): Promise<void> {
    if (this.state.flow?.step !== "resetDone") return Promise.resolve();
    return this.establishFromSession();
  }

  /**
   * The second step after an OAuth redirect that came back with ?twoFactor=1 (the challenge cookie is set; no
   * session yet). Also used by the platform for any other first step that ended in the second.
   */
  showTwoFactor(): void {
    if (this.state.status === "signedIn" || this.deps.password === undefined) return;
    this.patchSignIn({ flow: { step: "twoFactor", backup: false, trustDevice: true }, status: "signedOut" });
  }

  /** Two-step: a backup code instead of the authenticator (and back). */
  setBackupMode(backup: boolean): void {
    const flow = this.state.flow;
    if (flow?.step === "twoFactor") this.patchSignIn({ flow: { ...flow, backup } });
  }

  /** "Trust this device for 30 days". */
  setTrustDevice(on: boolean): void {
    const flow = this.state.flow;
    if (flow?.step === "twoFactor") this.update({ flow: { ...flow, trustDevice: on } });
  }

  /** Two-step: verifies the authenticator (6 digits; the sixth submits) or backup code, then signs in. */
  async verifySecondFactor(code: string): Promise<void> {
    const p = this.deps.password;
    const flow = this.state.flow;
    if (p === undefined || flow?.step !== "twoFactor" || this.state.busy !== undefined) return;
    // Authenticator codes lose spaces and dashes; backup codes go as issued ("ve1H4-DZxZU": Better Auth compares exactly).
    const typed = flow.backup ? code.trim() : normalizeCode(code);
    const wrong: SignInError = flow.backup ? "backupCode" : "twoStepCode";
    if (flow.backup ? normalizeCode(typed).length < 6 : !/^\d{6}$/.test(typed)) {
      this.update({ error: wrong });
      return;
    }
    this.patchSignIn({ busy: "verifying" });
    try {
      await p.verifySecondFactor(typed, { backup: flow.backup, trustDevice: flow.trustDevice });
    } catch (e) {
      const error = this.flowError(e, wrong);
      if (error === "signInExpired") {
        // The challenge ended (10 minutes, or 5 wrong codes): start again.
        const next: SessionState = { ...this.state, error, emailStep: "email" };
        delete next.flow;
        delete next.busy;
        this.setState(next);
        return;
      }
      this.patchSignIn(error === "locked" ? { error, lockedMinutes: LOCKOUT_MIN } : { error });
      return;
    }
    if (flow.afterReset !== undefined) this.patchSignIn({ flow: { step: "resetDone", ...flow.afterReset } });
    else await this.establishFromSession();
  }

  /** "Sign in with a passkey": waiting behind the browser/OS sheet, then signed in (never a second step). */
  async signInWithPasskey(): Promise<void> {
    const p = this.deps.password;
    if (p === undefined || this.state.busy !== undefined) return;
    this.patchSignIn({ busy: "passkey", flow: { step: "passkey", status: "waiting" } });
    try {
      await p.signInWithPasskey();
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "";
      if (this.state.flow?.step !== "passkey") return; // cancelled from the page
      if (code === "passkey_aborted") return;
      if (code === "passkey_cancelled") this.patchSignIn({ flow: { step: "passkey", status: "cancelled" } });
      else if (code === "passkey_not_found") this.patchSignIn({ flow: { step: "passkey", status: "notFound" } });
      else this.patchSignIn({ flow: { step: "passkey", status: "cancelled" }, error: signInError(e) });
      return;
    }
    await this.establishFromSession();
  }

  /**
   * Passkey autofill on the email field (conditional mediation), started when the methods show. Quiet: nothing is
   * shown unless the person picks a passkey from the browser's list (then: signed in, or "No passkey for Bundle here").
   */
  async startPasskeyAutofill(): Promise<void> {
    const p = this.deps.password;
    if (p === undefined || !this.canUsePasskey || this.state.status !== "signedOut") return;
    try {
      await p.signInWithPasskey({ autofill: true });
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "";
      if (code === "passkey_not_found" && this.state.status === "signedOut" && this.state.flow === undefined) {
        this.patchSignIn({ flow: { step: "passkey", status: "notFound" } });
      }
      return;
    }
    if (this.state.status === "signedOut") await this.establishFromSession();
  }

  /** A first step (email code, password) finished: signed in, or the second step. */
  private async afterFirstStep(step: SignInStep): Promise<void> {
    if (step.step === "twoFactor" && this.deps.password !== undefined) {
      this.patchSignIn({ flow: { step: "twoFactor", backup: false, trustDevice: true } });
      return;
    }
    await this.establishFromSession();
  }

  /** The address the session will carry when the access token has none. */
  private codeEmailFor(email: string): void {
    if (this.state.codeEmail !== email) this.update({ codeEmail: email });
  }

  /** A failed password / two-step / reset call → what the step shows. */
  private flowError(e: unknown, fallback: SignInError): SignInError {
    const kind = errorKind(e);
    if (kind === "network") return "network";
    const code = e instanceof ApiFailure ? e.code : undefined;
    switch (securityErrorFor(code)) {
      case "locked":
        return "locked";
      case "challengeExpired":
        return "signInExpired";
      case "rateLimited":
        return "rateLimited";
      default:
        return kind === "rateLimited" ? "rateLimited" : kind === "server" ? "unavailable" : fallback;
    }
  }

  /** Google: the web leaves the page (and comes back to `returnTo`); native finishes here. */
  async signInWithGoogle(returnTo: string): Promise<void> {
    if (this.state.busy !== undefined) return;
    this.patchSignIn({ busy: "google" });
    try {
      const result = await this.deps.auth.signInWithGoogle(returnTo);
      if (result === "signedIn") await this.establishFromSession();
    } catch (e) {
      this.patchSignIn({ error: signInError(e) });
    }
  }

  /** This platform can sign in with Apple (its AuthClient implements it); the server must offer it too (`methods.apple`). */
  get canSignInWithApple(): boolean {
    return this.deps.auth.signInWithApple !== undefined;
  }

  /** Sign in with Apple (when the platform supports it): like Google, the web leaves the page; native finishes here. */
  async signInWithApple(returnTo: string): Promise<void> {
    const signIn = this.deps.auth.signInWithApple;
    if (signIn === undefined || this.state.busy !== undefined) return;
    this.patchSignIn({ busy: "apple" });
    try {
      const result = await signIn.call(this.deps.auth, returnTo);
      if (result === "signedIn") await this.establishFromSession();
    } catch (e) {
      this.patchSignIn({ error: signInError(e) });
    }
  }

  /** Dev login (local development only): access token for an email, no Better Auth session. */
  async devSignIn(email: string, name: string): Promise<void> {
    const trimmed = email.trim();
    if (trimmed === "" || this.state.busy !== undefined) return;
    this.patchSignIn({ busy: "dev" });
    try {
      const login = await this.deps.api.devLogin(trimmed, name.trim() === "" ? undefined : name.trim());
      this.signedIn({ token: login.token, expiresAt: login.expiresAt, userId: login.userId, email: trimmed.toLowerCase(), kind: "dev" });
    } catch (e) {
      this.deps.api.setAuthToken(undefined);
      this.patchSignIn({ error: signInError(e) });
    }
  }

  /**
   * Browser sign-in (desktop): opens the system browser on the sign-in page. Again while waiting = "Try again" (a new
   * attempt replaces the old one). The browser hands the result back to the platform, which reports it (browserChanged).
   */
  async startBrowserSignIn(): Promise<void> {
    const browser = this.deps.auth.browser;
    if (browser === undefined || this.state.status !== "signedOut" || this.state.busy !== undefined) return;
    this.patchSignIn({ busy: "browser" });
    try {
      await browser.start();
    } catch (e) {
      this.patchSignIn({ error: browserSignInError(e), browser: { waiting: false } });
      return;
    }
    if (this.state.status === "signedOut") this.patchSignIn({ browser: { waiting: true } });
  }

  /** Browser sign-in, "Paste a code": the code the browser page shows (or its whole link), when the app didn't open. */
  async completeBrowserSignIn(code: string): Promise<void> {
    const browser = this.deps.auth.browser;
    const input = code.trim();
    if (browser === undefined || this.state.status !== "signedOut" || this.state.busy !== undefined) return;
    if (input === "") {
      this.update({ error: "invalidCode" });
      return;
    }
    this.patchSignIn({ busy: "verifying" });
    try {
      await browser.complete(input);
    } catch (e) {
      this.patchSignIn({ error: browserSignInError(e) });
      return;
    }
    // The platform reports the new session too (browserChanged); establish it here unless that already started.
    if (this.state.status === "signedOut") await this.establishFromSession();
  }

  /**
   * What the platform's browser sign-in reports, from any window: a new session (the browser handed back its code),
   * signed out (in another window, or the session was revoked), an attempt started, or an attempt failed.
   */
  private browserChanged(s: BrowserSignInStatus): void {
    const current = this.state.session;
    if (s.signedIn) {
      if (this.state.status === "signedIn" && current !== undefined && (s.userId === undefined || s.userId === current.userId)) return;
      // Someone else signed in from another window: drop this window's session before taking theirs.
      if (this.state.status === "signedIn") this.endSession(false);
      if (this.state.status === "signedOut") void this.establishFromSession();
      return;
    }
    if (this.state.status === "signedIn") this.endSession(false);
    const patch: Partial<SessionState> = { browser: { waiting: s.signingIn } };
    if (s.error !== undefined) patch.error = fromBrowserError(s.error);
    else if (this.state.error !== undefined && !s.signingIn) patch.error = this.state.error;
    this.patchSignIn(patch);
  }

  /**
   * Startup without a stored session: pick up an existing server session (web: the cookie set by an OAuth redirect
   * or another tab). Quietly stays signed out when there is none or the network is down.
   */
  async resume(): Promise<void> {
    if (this.state.status !== "signedOut" || this.state.busy !== undefined) return;
    this.update({ status: "signingIn" });
    let token: string;
    try {
      token = await this.deps.auth.accessToken();
    } catch {
      const status = this.state.status as SessionState["status"];
      if (status === "signingIn") this.update({ status: "signedOut" });
      return;
    }
    this.establish(token);
  }

  /** An error reported outside the view model (e.g. the OAuth redirect came back with an error). */
  reportError(error: SignInError): void {
    this.patchSignIn({ error });
  }

  private async establishFromSession(): Promise<void> {
    this.update({ status: "signingIn" });
    let token: string;
    try {
      token = await this.deps.auth.accessToken();
    } catch (e) {
      this.patchSignIn({ error: signInError(e), status: "signedOut" });
      return;
    }
    this.establish(token);
  }

  private establish(token: string): void {
    const claims = accessTokenClaims(token);
    if (claims === undefined) {
      this.patchSignIn({ error: "invalid", status: "signedOut" });
      return;
    }
    this.signedIn({ token, expiresAt: claims.expiresAt, userId: claims.userId, email: claims.email ?? this.state.codeEmail ?? "", kind: "session" });
  }

  private signedIn(next: Session): void {
    const prev = this.state.session;
    const session: Session = { ...next };
    if (prev !== undefined && prev.userId === next.userId && prev.workspace !== undefined) session.workspace = prev.workspace;
    const sameUser = prev !== undefined && prev.userId === next.userId;
    this.deps.api.setAuthToken(session.token);
    this.persist(session);
    this.lastRefreshAt = this.deps.clock.now();
    const state: SessionState = { ...this.state, status: "signedIn", session, emailStep: "email" };
    delete state.error;
    delete state.busy;
    delete state.codeEmail;
    delete state.flow;
    delete state.lockedMinutes;
    delete state.signInEmail;
    if (this.signingUp) state.signup = { step: "secure" };
    else if (state.signup?.step !== "secure") delete state.signup;
    if (!sameUser) {
      const list = parseWorkspaces(this.deps.storage.get(WORKSPACES_KEY), session.userId);
      if (list === undefined) delete state.workspaces;
      else state.workspaces = list;
    }
    this.setState(state);
    this.scheduleRefresh(session.expiresAt);
    void this.loadWorkspaces();
  }

  /** Applies a sign-in-flow patch; `error` is cleared unless the patch sets it, `busy` unless it sets it. */
  private patchSignIn(patch: Partial<SessionState>): void {
    const next: SessionState = { ...this.state, ...patch };
    if (!("error" in patch)) delete next.error;
    if (!("busy" in patch)) delete next.busy;
    this.setState(next);
  }

  // ---------- Access token ----------

  private scheduleRefresh(expiresAt: number, delayOverride?: number): void {
    this.cancelRefresh?.();
    const delay = delayOverride ?? refreshDelay(expiresAt, this.deps.clock.now());
    this.cancelRefresh = this.deps.clock.setTimer(delay, () => {
      this.cancelRefresh = undefined;
      void this.refresh();
    });
  }

  /**
   * Gets a new access token (single flight). Resolves true when renewed. Signs out when the session is gone;
   * keeps the session (and retries later) when the network is down.
   */
  refresh(): Promise<boolean> {
    const s = this.state.session;
    if (this.state.status !== "signedIn" || s === undefined) return Promise.resolve(false);
    this.refreshing ??= this.renew(s).finally(() => {
      this.refreshing = undefined;
    });
    return this.refreshing;
  }

  private async renew(s: Session): Promise<boolean> {
    let token: string;
    let expiresAt: number;
    try {
      if (s.kind === "dev") {
        const login = await this.deps.api.devLogin(s.email, undefined);
        if (login.userId !== s.userId) throw new ApiFailure("auth", 401, "different_user", "different user");
        token = login.token;
        expiresAt = login.expiresAt;
      } else {
        token = await this.deps.auth.accessToken();
        const claims = accessTokenClaims(token);
        if (claims === undefined || claims.userId !== s.userId) throw new ApiFailure("auth", 401, "different_user", "different user");
        expiresAt = claims.expiresAt;
      }
    } catch (e) {
      if (this.state.session !== s) return false;
      if (errorKind(e) === "network" || errorKind(e) === "server") {
        // Offline or the server is down: keep the session (cached content stays usable) and try again later.
        this.scheduleRefresh(s.expiresAt, REFRESH_RETRY_MS);
        return false;
      }
      this.signOut();
      return false;
    }
    const current = this.state.session;
    if (current === undefined || current.userId !== s.userId) return false;
    const session: Session = { ...current, token, expiresAt };
    this.deps.api.setAuthToken(token);
    this.persist(session);
    this.lastRefreshAt = this.deps.clock.now();
    this.update({ session });
    this.scheduleRefresh(expiresAt);
    return true;
  }

  /**
   * The server rejected an access token (only ever learned while online): refresh once, then the caller retries.
   * `rejectedToken` (when the caller knows it): a stale token just means "retry with the current one"; the current
   * token being rejected right after a successful refresh signs out instead of looping. Resolves true when the
   * caller should retry.
   */
  authRejected(rejectedToken?: string): Promise<boolean> {
    const s = this.state.session;
    if (this.state.status !== "signedIn" || s === undefined) return Promise.resolve(false);
    if (rejectedToken !== undefined && rejectedToken !== s.token) return Promise.resolve(true);
    if (this.refreshing !== undefined) return this.refreshing;
    if (this.deps.clock.now() - this.lastRefreshAt < MIN_REJECT_INTERVAL_MS) {
      if (rejectedToken === undefined) return Promise.resolve(true);
      this.signOut();
      return Promise.resolve(false);
    }
    return this.refresh();
  }

  /** Before an authenticated request: refreshes first when the access token is expired or about to be. */
  async ensureFreshToken(): Promise<void> {
    const s = this.state.session;
    if (this.state.status !== "signedIn" || s === undefined) return;
    if (this.refreshing !== undefined) {
      await this.refreshing;
      return;
    }
    if (needsRefresh(s.expiresAt, this.deps.clock.now())) await this.refresh();
  }

  signOut(): void {
    this.endSession(true);
  }

  /** Forgets the session here; `revoke` also ends it on the server (and, on desktop, for every window). */
  private endSession(revoke: boolean): void {
    const s = this.state.session;
    if (revoke && s !== undefined && s.kind === "session") {
      this.deps.auth.signOut().catch(() => {
        // Best effort: the local session is gone either way.
      });
    }
    this.cancelRefresh?.();
    this.cancelRefresh = undefined;
    this.deps.storage.remove(SESSION_KEY);
    this.deps.storage.remove(WORKSPACES_KEY);
    this.deps.api.setAuthToken(undefined);
    const next: SessionState = { status: "signedOut", methodsFailed: this.state.methodsFailed, emailStep: "email", workspacesLoading: false, creatingWorkspace: false };
    if (this.state.methods !== undefined) next.methods = this.state.methods;
    if (this.state.browser !== undefined) next.browser = { waiting: false };
    if (this.state.inviteToken !== undefined) next.inviteToken = this.state.inviteToken;
    this.setState(next);
  }

  // ---------- Workspaces ----------

  /** Refreshes the workspace list (background; failures keep the cached list). */
  async loadWorkspaces(): Promise<void> {
    const s = this.state.session;
    if (s === undefined || this.state.workspacesLoading) return;
    this.update({ workspacesLoading: true });
    try {
      const list = (await this.deps.api.myWorkspaces()).map(toRef);
      if (this.state.session?.userId !== s.userId) return;
      this.setWorkspaces(list);
      this.update({ workspacesLoading: false });
    } catch (e) {
      this.update({ workspacesLoading: false });
      if (errorKind(e) === "auth" && (await this.authRejected())) await this.loadWorkspaces();
      return;
    }
    await this.loadJoinable();
  }

  /** Refreshes the workspaces I can join by email domain (background; failures keep what was there). */
  async loadJoinable(): Promise<void> {
    const s = this.state.session;
    if (s === undefined) return;
    try {
      const joinable = await this.deps.api.joinableWorkspaces();
      if (this.state.session?.userId !== s.userId) return;
      const mine = new Set((this.state.workspaces ?? []).map((w) => w.id));
      this.update({ joinable: joinable.filter((w) => !mine.has(w.id)) });
    } catch {
      // Offline or an older server: nothing to offer.
    }
  }

  /**
   * Joins a workspace that allows my email's domain (as a member) and opens it. Throws on failure (offline, or the
   * server refused: the list is refreshed so a stale entry disappears).
   */
  async joinWorkspace(workspaceId: string): Promise<WorkspaceRef> {
    if (this.state.joining !== undefined) throw new ApiFailure("invalid", 409, "busy", "already joining");
    this.update({ joining: workspaceId });
    try {
      const res = await this.deps.api.joinWorkspace(workspaceId);
      const ref = toRef(res.workspace);
      this.addWorkspace(ref);
      this.update({ joinable: (this.state.joinable ?? []).filter((w) => w.id !== workspaceId) });
      this.openWorkspace(ref);
      return ref;
    } catch (e) {
      if (errorKind(e) !== "network") void this.loadJoinable();
      throw e;
    } finally {
      const next = { ...this.state };
      delete next.joining;
      this.setState(next);
    }
  }

  /** Opens a workspace (switcher, URL, after create/accept). */
  openWorkspace(ws: WorkspaceRef): void {
    const s = this.state.session;
    if (s === undefined) return;
    const cur = s.workspace;
    if (cur !== undefined && cur.id === ws.id && cur.slug === ws.slug && cur.name === ws.name && !this.state.creatingWorkspace) return;
    this.deps.onWorkspaceOpened?.({ id: ws.id, slug: ws.slug, name: ws.name });
    const session: Session = { ...s, workspace: { id: ws.id, slug: ws.slug, name: ws.name } };
    this.persist(session);
    this.update({ session, creatingWorkspace: false });
  }

  /** Keeps the cached workspace name/slug fresh after a bootstrap. */
  rememberWorkspace(ws: WorkspaceRef): void {
    const s = this.state.session;
    if (s === undefined || s.workspace?.id !== ws.id) return;
    if (s.workspace.slug === ws.slug && s.workspace.name === ws.name) return;
    this.openWorkspace(ws);
    const list = this.state.workspaces;
    if (list !== undefined) this.setWorkspaces(list.map((w) => (w.id === ws.id ? { id: ws.id, slug: ws.slug, name: ws.name } : w)));
  }

  startCreatingWorkspace(): void {
    this.update({ creatingWorkspace: true });
  }

  /** Back to the current workspace (only when there is one). */
  cancelCreatingWorkspace(): void {
    this.update({ creatingWorkspace: false });
  }

  /** Creates a workspace (online only), opens it in #general and queues the "Invite people" step. Throws on failure. */
  async createWorkspace(name: string): Promise<CreateWorkspaceResponse> {
    const res = await this.deps.api.createWorkspace({ name: name.trim() });
    const ref = toRef(res.workspace);
    this.addWorkspace(ref);
    this.update({ welcome: { workspaceId: ref.id, generalChannelId: res.generalChannelId } });
    this.openWorkspace(ref);
    return res;
  }

  /** The "Invite people" step was finished or skipped. */
  finishWelcome(): void {
    if (this.state.welcome === undefined) return;
    const next = { ...this.state };
    delete next.welcome;
    this.setState(next);
  }

  // ---------- Invites ----------

  /** Remembers the invite from /invite/<token> (survives sign-in and the OAuth redirect). */
  setInviteToken(token: string | undefined): void {
    if (token === this.state.inviteToken) return;
    if (token === undefined) this.deps.storage.remove(INVITE_KEY);
    else this.deps.storage.set(INVITE_KEY, token);
    const next = { ...this.state };
    if (token === undefined) delete next.inviteToken;
    else next.inviteToken = token;
    this.setState(next);
  }

  /** Joins the invited workspace (signed in) and opens it. Throws on failure (the token stays for a retry). */
  async acceptInvite(): Promise<WorkspaceRef | undefined> {
    const token = this.state.inviteToken;
    if (token === undefined || this.state.status !== "signedIn") return undefined;
    const res = await this.deps.api.acceptInvite(token);
    const ref = toRef(res.workspace);
    this.addWorkspace(ref);
    this.openWorkspace(ref);
    this.setInviteToken(undefined);
    return ref;
  }

  // ---------- Persistence ----------

  private addWorkspace(ref: WorkspaceRef): void {
    const list = (this.state.workspaces ?? []).filter((w) => w.id !== ref.id);
    this.setWorkspaces([...list, ref]);
  }

  private setWorkspaces(list: WorkspaceRef[]): void {
    const s = this.state.session;
    if (s === undefined) return;
    const sorted = list.toSorted(byName);
    this.deps.storage.set(WORKSPACES_KEY, JSON.stringify({ userId: s.userId, workspaces: sorted }));
    this.update({ workspaces: sorted });
  }

  private persist(session: Session): void {
    // Browser sign-in: the platform keeps the session and hands out access tokens on demand, so none is stored.
    const saved: Session = this.deps.auth.browser === undefined ? session : { ...session, token: "", expiresAt: 0 };
    this.deps.storage.set(SESSION_KEY, JSON.stringify(saved));
  }
}
